import type { ReactNode } from 'react';

import { LoadError } from '@/components/ui/load-state';
import { formatDate } from '@/lib/format';

import type { Grant } from './permissionsApi';

/** A back link row for a detail view. */
export function BackLink({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="mb-4 text-sm text-muted-foreground hover:text-foreground">
      ← {label}
    </button>
  );
}

/** A refusal or failure inside a view, with the service's text. */
export function Problem({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return <LoadError message={message} onRetry={onRetry} />;
}

export const untilText = (iso: string | null | undefined) => (iso ? `until ${formatDate(iso)}` : 'no end date');

/** The person or group of a grant: the email when the service sent one, else the id (`user:*` is everyone). */
export function whoOf(grant: Grant): string {
  if (grant.subject === 'user:*') return 'Everyone';
  return grant.subject_label || grant.subject;
}

export function givenBy(grant: Grant): string {
  return grant.author_label || grant.author;
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm text-foreground">{children}</dd>
    </div>
  );
}

/** `integration_level:slack.read` as "slack read"; any other object keeps its id. */
export function objectText(object: string): string {
  const [type, id = ''] = object.split(/:(.*)/s);
  return type === 'integration_level' ? id.replace('.', ' ') : object;
}
